'use client';

import React, { useState, useEffect } from 'react';
import { Bell, PlayCircle, RefreshCw, CheckCircle2, Clock, XCircle, AlertCircle, ShieldAlert } from 'lucide-react';

export function ScheduledNotificationsQueue() {
  const [jobs, setJobs] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/cron/notifications');
      const data = await res.json();
      if (data.success && data.notifications) {
        setJobs(data.notifications);
      }
    } catch (err) {
      console.warn('Error fetching scheduled notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();
  }, []);

  const handleRunWorker = async () => {
    setRunning(true);
    setMessage(null);
    try {
      const res = await fetch('/api/cron/notifications', { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        setMessage(`Se procesaron ${data.processedCount || 0} notificación(es) en el motor asíncrono.`);
        fetchJobs();
      } else {
        setMessage('Error al ejecutar worker: ' + data.error);
      }
    } catch (err: any) {
      setMessage('Error de comunicación: ' + err.message);
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200/90 rounded-3xl p-6 sm:p-7 shadow-xs space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-slate-100">
        <div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center font-bold">
              <Bell className="w-4 h-4" />
            </div>
            <h3 className="font-extrabold text-[#0f172a] text-base">
              Cola de Notificaciones Programadas (Motor Asíncrono)
            </h3>
          </div>
          <p className="text-xs text-slate-500 mt-1">
            Persistencia en PostgreSQL, cálculo por zona horaria de negocio y proveedor simulado para pruebas.
          </p>
        </div>

        <div className="flex items-center gap-2 self-start sm:self-auto">
          <button
            type="button"
            disabled={loading}
            onClick={fetchJobs}
            className="p-2 text-slate-500 hover:text-slate-800 bg-slate-100 hover:bg-slate-200 rounded-xl transition cursor-pointer"
            title="Actualizar lista"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>

          <button
            type="button"
            disabled={running}
            onClick={handleRunWorker}
            className="px-4 py-2 bg-[#047857] hover:bg-[#065f46] text-white rounded-xl text-xs font-bold transition flex items-center gap-1.5 cursor-pointer disabled:opacity-50 shadow-xs"
          >
            <PlayCircle className="w-3.5 h-3.5" />
            <span>{running ? 'Ejecutando...' : 'Ejecutar / Simular pendientes'}</span>
          </button>
        </div>
      </div>

      {/* Alerta de modo seguro de pruebas */}
      <div className="bg-blue-50 border border-blue-200/80 rounded-2xl p-3.5 text-xs text-blue-900 flex items-start gap-2.5">
        <ShieldAlert className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
        <div>
          <span className="font-bold block">Entorno Seguro de Pruebas</span>
          <span className="text-blue-700 leading-relaxed block mt-0.5 text-[11px]">
            Las notificaciones se programan y procesan de forma verídica en la base de datos PostgreSQL. En esta fase se utiliza el proveedor simulado (SimulatedWhatsAppProvider) para garantizar que ningún mensaje se envíe a teléfonos de clientes reales.
          </span>
        </div>
      </div>

      {message && (
        <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-2xl text-xs font-semibold text-emerald-800">
          {message}
        </div>
      )}

      {/* Lista de Trabajos */}
      {loading && jobs.length === 0 ? (
        <div className="py-8 text-center text-xs text-slate-400">
          Consultando cola de notificaciones en PostgreSQL...
        </div>
      ) : jobs.length === 0 ? (
        <div className="py-8 text-center text-xs text-slate-400">
          No hay notificaciones encoladas actualmente.
        </div>
      ) : (
        <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
          {jobs.map((job) => {
            const isSimulated = job.status === 'SIMULATED';
            const isPending = job.status === 'PENDING';
            const isCancelled = job.status === 'CANCELLED';
            const isFailed = job.status === 'FAILED';

            const badgeClass = isSimulated
              ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
              : isPending
              ? 'bg-amber-100 text-amber-800 border-amber-300'
              : isCancelled
              ? 'bg-slate-200 text-slate-600 border-slate-300'
              : 'bg-rose-100 text-rose-800 border-rose-300';

            const statusLabel = isSimulated
              ? 'Simulado (Entorno de pruebas)'
              : isPending
              ? 'Pendiente de emisión'
              : isCancelled
              ? 'Cancelado'
              : 'Fallido';

            return (
              <div
                key={job.id}
                className="p-3 bg-slate-50/80 border border-slate-200/70 rounded-2xl flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs"
              >
                <div>
                  <div className="flex items-center gap-2">
                    <strong className="text-slate-900 font-bold">
                      {job.type.replace(/_/g, ' ')}
                    </strong>
                    <span className="text-slate-400 text-[11px]">
                      · Para {job.recipientName} ({job.recipientPhone})
                    </span>
                  </div>
                  <p className="text-[11px] text-slate-500 mt-0.5 line-clamp-1">
                    "{job.messageBody}"
                  </p>
                  <span className="text-[10px] text-slate-400 block mt-1">
                    Programado para: {new Date(job.scheduledFor).toLocaleString('es-AR')}
                    {job.sentAt ? ` · Procesado: ${new Date(job.sentAt).toLocaleString('es-AR')}` : ''}
                  </span>
                </div>

                <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase border self-start sm:self-auto shrink-0 ${badgeClass}`}>
                  {statusLabel}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
